import { t } from '@trimble/i18n';
import type { LengthUnit } from '@fulcrum/lib';
import { Component, createMemo, For, Show, type JSX } from 'solid-js';
import type { EditablePalette, EditableStop } from '../../types/heatmap.types';
import {
  convertStopValueToDisplay,
  EDITABLE_STOP_LAYOUT,
  formatOverflowLabel,
  formatStopValueForDisplay,
  overflowAboveRowTopPx,
  overflowBelowRowTopPx,
  overflowStopColors,
  sortStopsByValueDescending,
  stopListHeightPx,
  stopRowTopPxMap,
  stopValueRange,
  STOP_ROW_HEIGHT_PX,
  VIEWER_STOP_LAYOUT,
} from '../../heatmap-stop-layout';
import { GradientStopBar } from './GradientStopBar';
import { OverflowStopRow } from './OverflowStopRow';
import { StopValueRow } from './StopValueRow';
import type { PaletteStopColorPickerGroup } from './createPaletteStopColorPickerControls';
import { HeatmapSectionLabel } from '../../components/HeatmapSectionLabel';
import { PALETTE_STOP_MODUS_OVERRIDES } from './palette-stop-modus-overrides';

/** The measurements API requires a palette to keep at least two stops. */
const MIN_STOP_COUNT = 2;
const FLIP_DURATION_MS = 250;
const MIN_MOVEMENT_PX = 1;

/**
 * Snapshot the vertical position of every stop-row inside `container`,
 * keyed by `data-stop-id`.
 */
function snapshotStopPositions(container: HTMLElement): Map<string, number> {
  const positions = new Map<string, number>();
  const rows = container.querySelectorAll<HTMLElement>('[data-stop-id]');
  rows.forEach((row) => {
    const id = row.dataset['stopId'];
    if (id) positions.set(id, row.getBoundingClientRect().top);
  });
  return positions;
}

/**
 * FLIP animate stop-rows from their `firstPositions` to their current
 * (post-sort) positions inside `container`.
 */
function flipAnimateStops(
  container: HTMLElement,
  firstPositions: Map<string, number>
): void {
  requestAnimationFrame(() => {
    const rows = container.querySelectorAll<HTMLElement>('[data-stop-id]');
    rows.forEach((row) => {
      const id = row.dataset['stopId'];
      if (!id) return;
      const firstTop = firstPositions.get(id);
      if (firstTop === undefined) return;

      const delta = firstTop - row.getBoundingClientRect().top;
      if (Math.abs(delta) < MIN_MOVEMENT_PX) return;

      // Invert: jump to the old position with no transition.
      row.style.transform = `translateY(${String(delta)}px)`;
      row.style.transition = 'none';

      // Play: animate back to the new position.
      requestAnimationFrame(() => {
        row.style.transition = `transform ${String(FLIP_DURATION_MS)}ms ease`;
        row.style.transform = '';

        const onEnd = (): void => {
          row.style.transition = '';
          row.removeEventListener('transitionend', onEnd);
        };
        row.addEventListener('transitionend', onEnd);
      });
    });
  });
}

export interface PaletteStopViewerProps {
  readonly palette: EditablePalette;
  readonly lengthUnit?: LengthUnit;
  readonly editable?: boolean;
  /**
   * Optional coordinator shared across the panel's colour pickers so opening
   * one stop-row's picker closes any other open picker.
   */
  readonly colorPickerGroup?: PaletteStopColorPickerGroup;
  /** Commit a stop value edit (blur / Enter); parent updates and re-sorts in one write. */
  onStopValueCommit?(id: string, value: string): void;
  onRemoveStop?(id: string): void;
  onStopColorChange?(id: string, color: string): void;
  /** Change the out-of-range-above cap colour. */
  onAboveColorChange?(color: string): void;
  /** Change the out-of-range-below cap colour (gradient only). */
  onBelowColorChange?(color: string): void;
}

/**
 * Palette stop column: vertical bar, overflow rows, and value rows. Read-only by
 * default; passing `editable` plus callbacks turns the value rows into editable
 * inputs (with remove controls) and animates re-sorting on commit. The section
 * label is omitted in editable mode because `EditPalettePanel` supplies it.
 */
export const PaletteStopViewer: Component<PaletteStopViewerProps> = (props) => {
  let stopRowsContainer: HTMLDivElement | undefined;

  const lengthUnit = (): LengthUnit => props.lengthUnit ?? 'm';

  const stops = (): readonly EditableStop[] => props.palette.stops;

  const canRemoveStop = (): boolean => stops().length > MIN_STOP_COUNT;

  function handleStopValueCommit(stopId: string, value: string): void {
    const first = stopRowsContainer
      ? snapshotStopPositions(stopRowsContainer)
      : undefined;

    // Snapshot must happen before the store update — Solid re-sorts <For> synchronously.
    props.onStopValueCommit?.(stopId, value);

    if (first && stopRowsContainer && first.size > 0) {
      flipAnimateStops(stopRowsContainer, first);
    }
  }

  const sortedStops = createMemo(() => sortStopsByValueDescending(stops()));

  const valueRange = createMemo(() => stopValueRange(stops()));

  const overflowColors = createMemo(() =>
    overflowStopColors(
      stops(),
      {
        above: props.palette.colorAboveRange,
        below: props.palette.colorBelowRange,
      },
      props.palette.textureType
    )
  );

  const overflowLabels = createMemo(() => {
    const unit = lengthUnit();
    return {
      above: formatOverflowLabel(
        'above',
        convertStopValueToDisplay(valueRange().max, unit),
        unit
      ),
      below: formatOverflowLabel(
        'below',
        convertStopValueToDisplay(valueRange().min, unit),
        unit
      ),
    };
  });

  const listHeightPx = (): number => stopListHeightPx(stops().length);

  const stopRowTopPxById = createMemo(() =>
    stopRowTopPxMap(stops(), props.palette.textureType, {
      // Editable mode clamps clustered rows to the row height so inputs and the
      // remove control never overlap; the read-only viewer keeps the
      // value-proportional layout that aligns with the bar.
      ...(props.editable ? EDITABLE_STOP_LAYOUT : VIEWER_STOP_LAYOUT),
      textureType: props.palette.textureType,
    })
  );

  const overflowBelowTopPx = (): number =>
    overflowBelowRowTopPx(stops().length);

  const overflowRowStyle = (topPx: number): JSX.CSSProperties => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: `${String(topPx)}px`,
    height: `${String(STOP_ROW_HEIGHT_PX)}px`,
    display: 'flex',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'align-items': 'center',
  });

  return (
    <>
      <style>{PALETTE_STOP_MODUS_OVERRIDES}</style>
      <div
        class={`palette-stop-viewer flex flex-col gap-1${
          props.editable ? ' pt-4' : ''
        }`}
        data-testid="palette-stop-viewer"
      >
        <Show when={!props.editable}>
          <HeatmapSectionLabel
            uppercase
            label={t('measurements.volumetric.gradient.colors')}
          />
        </Show>

        <div class="flex items-start gap-2" data-testid="palette-stop-rows">
          <GradientStopBar
            stops={stops()}
            textureType={props.palette.textureType}
            colorAboveRange={props.palette.colorAboveRange}
            colorBelowRange={props.palette.colorBelowRange}
          />

          <div
            class="relative flex-1 min-w-0"
            style={{
              position: 'relative',
              height: `${String(listHeightPx())}px`,
            }}
            ref={(el) => {
              stopRowsContainer = el;
            }}
          >
            <div style={overflowRowStyle(overflowAboveRowTopPx())}>
              <OverflowStopRow
                color={overflowColors().above}
                label={overflowLabels().above}
                testId="palette-overflow-above"
                editable={props.editable}
                colorPickerGroup={props.colorPickerGroup}
                onColorChange={props.onAboveColorChange}
              />
            </div>

            <For each={sortedStops()}>
              {(stop) => (
                <div
                  class="absolute left-0 right-0 flex items-center"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: `${String(stopRowTopPxById().get(stop.id) ?? 0)}px`,
                    height: `${String(STOP_ROW_HEIGHT_PX)}px`,
                  }}
                  data-stop-id={stop.id}
                >
                  <StopValueRow
                    color={stop.color}
                    initialValue={formatStopValueForDisplay(
                      stop.value,
                      lengthUnit()
                    )}
                    lengthUnit={lengthUnit()}
                    testId={`palette-stop-${stop.id}`}
                    editable={props.editable}
                    colorPickerGroup={props.colorPickerGroup}
                    removeDisabled={!canRemoveStop()}
                    onValueCommit={(value) =>
                      handleStopValueCommit(stop.id, value)
                    }
                    onRemove={() => props.onRemoveStop?.(stop.id)}
                    onColorChange={(color) =>
                      props.onStopColorChange?.(stop.id, color)
                    }
                  />
                </div>
              )}
            </For>

            <Show when={props.palette.textureType !== 'stepped'}>
              <div style={overflowRowStyle(overflowBelowTopPx())}>
                <OverflowStopRow
                  color={overflowColors().below}
                  label={overflowLabels().below}
                  testId="palette-overflow-below"
                  editable={props.editable}
                  colorPickerGroup={props.colorPickerGroup}
                  onColorChange={props.onBelowColorChange}
                />
              </div>
            </Show>
          </div>
        </div>
      </div>
    </>
  );
};
